import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { KeyRound, MessageCircle, ShieldCheck, Cake, Send, RefreshCcw } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { inputCls, PageHeader } from "./shared";

const WA_STATUS_STYLES = {
  sent: "bg-emerald-600/10 text-emerald-700",
  failed: "bg-red-100 text-red-600",
  skipped: "bg-gray-100 text-gray-500",
};

export default function Settings() {
  const [pins, setPins] = useState({ ceo_pin: "", manager_pin: "", staff_pin: "" });
  const [recoveryKey, setRecoveryKey] = useState(null);
  const [wa, setWa] = useState({ access_token: "", phone_number_id: "", confirmation_template: "", receipt_template: "", greeting_template: "", language: "en" });
  const [waStatus, setWaStatus] = useState(null);
  const [greet, setGreet] = useState({ birthday: "", anniversary: "" });
  const [test, setTest] = useState({ to: "", mode: "text" });
  const [waLog, setWaLog] = useState([]);

  const loadWa = useCallback(() => {
    api.get("/settings/whatsapp").then(({ data }) => {
      setWaStatus(data);
      setWa((w) => ({ ...w, confirmation_template: data.confirmation_template || "", receipt_template: data.receipt_template || "", greeting_template: data.greeting_template || "", language: data.language || "en" }));
    }).catch(() => {});
    api.get("/whatsapp/log").then(({ data }) => setWaLog(data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadWa();
    api.get("/settings/greetings").then(({ data }) => setGreet(data)).catch(() => {});
  }, [loadWa]);

  const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

  const savePins = async (e) => {
    e.preventDefault();
    if (!pins.ceo_pin && !pins.manager_pin && !pins.staff_pin) { toast.error("Enter at least one new PIN"); return; }
    try {
      const { data } = await api.post("/settings/pins", pins);
      toast.success(`PINs updated: ${data.changed.join(", ").toUpperCase()}`);
      setPins({ ceo_pin: "", manager_pin: "", staff_pin: "" });
    } catch (e2) { err(e2); }
  };

  const genKey = async () => {
    try {
      const { data } = await api.post("/settings/recovery-key/generate");
      setRecoveryKey(data.recovery_key);
      toast.success("Recovery key generated — save it now, it won't be shown again");
    } catch (e2) { err(e2); }
  };

  const saveWa = async (e) => {
    e.preventDefault();
    try {
      await api.post("/settings/whatsapp", wa);
      toast.success("WhatsApp settings saved");
      setWa((w) => ({ ...w, access_token: "", phone_number_id: "" }));
      loadWa();
    } catch (e2) { err(e2); }
  };

  const saveGreetings = async (e) => {
    e.preventDefault();
    try {
      await api.post("/settings/greetings", greet);
      toast.success("Greeting messages saved");
    } catch (e2) { err(e2); }
  };

  const sendTest = async (e) => {
    e.preventDefault();
    if (!test.to.trim()) { toast.error("Enter a phone number to test"); return; }
    try {
      await api.post("/whatsapp/test", test);
      toast.success("Test message sent! Check the phone.");
      loadWa();
    } catch (e2) { err(e2); loadWa(); }
  };

  const pinInput = (key, label, testid) => (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">{label}</label>
      <input data-testid={testid} type="password" inputMode="numeric" maxLength={4} className={inputCls} placeholder="New 4-digit PIN"
        value={pins[key]} onChange={(e) => setPins({ ...pins, [key]: e.target.value.replace(/\D/g, "").slice(0, 4) })} />
    </div>
  );

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <PageHeader title="Settings" subtitle="CEO-only: access PINs, recovery key, greetings and WhatsApp API." />

      <div className="bg-white border border-[#E5E7E5] rounded-md p-6 shadow-sm">
        <h2 className="flex items-center gap-2 text-sm font-bold text-[#1C1C1C] mb-4"><ShieldCheck className="w-4 h-4 text-[#C5A059]" /> Access Mode PINs</h2>
        <form onSubmit={savePins} className="grid sm:grid-cols-3 gap-4" data-testid="pins-form">
          {pinInput("ceo_pin", "CEO / Owner PIN", "ceo-pin-input")}
          {pinInput("manager_pin", "Manager PIN", "manager-pin-input")}
          {pinInput("staff_pin", "Staff PIN", "staff-pin-input")}
          <button type="submit" data-testid="save-pins-btn" className="sm:col-span-3 bg-[#0A3F2F] text-white font-semibold py-2.5 rounded-full text-sm hover:bg-[#0d5540]">
            Update PINs
          </button>
        </form>
        <p className="text-xs text-[#5C6A63] mt-3">Leave a field blank to keep that PIN unchanged. Initial PINs: CEO 9999 · Manager 5555 · Staff 1111 — change them now.</p>
      </div>

      <div className="bg-white border border-[#E5E7E5] rounded-md p-6 shadow-sm">
        <h2 className="flex items-center gap-2 text-sm font-bold text-[#1C1C1C] mb-2"><KeyRound className="w-4 h-4 text-[#C5A059]" /> PIN Recovery Key</h2>
        <p className="text-xs text-[#5C6A63] mb-4">If you ever forget your PINs, this one-time key unlocks CEO Mode. Generating a new key replaces the old one.</p>
        {recoveryKey && (
          <div className="bg-[#0A3F2F] text-[#C5A059] font-mono text-lg tracking-widest text-center rounded-md py-4 mb-4 select-all" data-testid="recovery-key-display">
            {recoveryKey}
          </div>
        )}
        <button onClick={genKey} data-testid="generate-recovery-btn" className="bg-[#C5A059] text-[#1C1C1C] font-bold text-sm px-5 py-2.5 rounded-full hover:bg-[#d4b06c]">
          Generate Recovery Key
        </button>
      </div>

      <div className="bg-white border border-[#E5E7E5] rounded-md p-6 shadow-sm">
        <h2 className="flex items-center gap-2 text-sm font-bold text-[#1C1C1C] mb-2"><Cake className="w-4 h-4 text-[#C5A059]" /> Birthday & Anniversary Greetings</h2>
        <p className="text-xs text-[#5C6A63] mb-4">These messages are sent from the Overview celebrations list. Write your own offer — <code className="bg-gray-100 px-1 rounded">{"{name}"}</code> is replaced with the client's name.</p>
        <form onSubmit={saveGreetings} className="space-y-3" data-testid="greetings-form">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Birthday message</label>
            <textarea data-testid="greeting-birthday-input" className={inputCls} rows={4} value={greet.birthday} onChange={(e) => setGreet({ ...greet, birthday: e.target.value })} />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Anniversary message</label>
            <textarea data-testid="greeting-anniversary-input" className={inputCls} rows={4} value={greet.anniversary} onChange={(e) => setGreet({ ...greet, anniversary: e.target.value })} />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Win-back message (clients away 30+ days)</label>
            <textarea data-testid="greeting-winback-input" className={inputCls} rows={4} value={greet.winback || ""} onChange={(e) => setGreet({ ...greet, winback: e.target.value })} />
          </div>
          <button type="submit" data-testid="save-greetings-btn" className="bg-[#0A3F2F] text-white font-semibold text-sm px-5 py-2.5 rounded-full hover:bg-[#0d5540]">
            Save Greetings
          </button>
        </form>
      </div>

      <div className="bg-white border border-[#E5E7E5] rounded-md p-6 shadow-sm">
        <h2 className="flex items-center gap-2 text-sm font-bold text-[#1C1C1C] mb-2"><MessageCircle className="w-4 h-4 text-[#C5A059]" /> WhatsApp Business API (Meta)</h2>
        <p className="text-xs text-[#5C6A63] mb-4" data-testid="wa-status">
          {waStatus?.configured
            ? `✅ Keys saved (token ${waStatus.access_token_masked}, phone ID ${waStatus.phone_number_id}). Automatic sending is ACTIVE: appointment confirmations, bill receipts and greetings go out via the API.`
            : "Not configured yet — one-click wa.me sending is active meanwhile. Paste your Meta keys here and automatic sending switches on instantly."}
        </p>
        <form onSubmit={saveWa} className="space-y-3" data-testid="wa-form">
          <input data-testid="wa-token-input" className={inputCls} placeholder={waStatus?.configured ? "Access token (leave blank to keep current)" : "WHATSAPP_ACCESS_TOKEN (permanent system-user token)"}
            value={wa.access_token} onChange={(e) => setWa({ ...wa, access_token: e.target.value })} />
          <input data-testid="wa-phoneid-input" className={inputCls} placeholder={waStatus?.configured ? "Phone Number ID (leave blank to keep current)" : "WHATSAPP_PHONE_NUMBER_ID"}
            value={wa.phone_number_id} onChange={(e) => setWa({ ...wa, phone_number_id: e.target.value })} />
          <p className="text-[11px] text-[#5C6A63] pt-1">Optional — approved template names from WhatsApp Manager. If left blank, plain text messages are used (these only deliver within 24h of the client's last message to you; templates deliver always).</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid="wa-confirmation-template-input" className={inputCls} placeholder="Confirmation template name" value={wa.confirmation_template} onChange={(e) => setWa({ ...wa, confirmation_template: e.target.value })} />
            <input data-testid="wa-receipt-template-input" className={inputCls} placeholder="Receipt template name" value={wa.receipt_template} onChange={(e) => setWa({ ...wa, receipt_template: e.target.value })} />
            <input data-testid="wa-greeting-template-input" className={inputCls} placeholder="Greeting template name" value={wa.greeting_template} onChange={(e) => setWa({ ...wa, greeting_template: e.target.value })} />
            <input data-testid="wa-language-input" className={inputCls} placeholder="Template language code (e.g. en, en_US, hi)" value={wa.language} onChange={(e) => setWa({ ...wa, language: e.target.value })} />
          </div>
          <button type="submit" data-testid="save-wa-btn" className="bg-[#0A3F2F] text-white font-semibold text-sm px-5 py-2.5 rounded-full hover:bg-[#0d5540]">
            Save WhatsApp Settings
          </button>
          {waStatus?.configured && (
            <button type="button" data-testid="clear-wa-keys-btn" onClick={async () => {
              try {
                await api.post("/settings/whatsapp", { ...wa, clear_keys: true });
                toast.success("WhatsApp keys removed — back to one-click sending");
                loadWa();
              } catch (e2) { err(e2); }
            }} className="ml-2 text-xs font-semibold text-red-600 underline underline-offset-4">
              Remove keys
            </button>
          )}
        </form>

        {waStatus?.configured && (
          <form onSubmit={sendTest} className="mt-5 pt-5 border-t border-[#E5E7E5] flex flex-wrap items-center gap-2" data-testid="wa-test-form">
            <input data-testid="wa-test-phone-input" className={`${inputCls} w-48`} placeholder="Phone to test (10 digits)" value={test.to} onChange={(e) => setTest({ ...test, to: e.target.value })} />
            <select data-testid="wa-test-mode-select" className={`${inputCls} w-56`} value={test.mode} onChange={(e) => setTest({ ...test, mode: e.target.value })}>
              <option value="text">Plain text message</option>
              <option value="hello_world">hello_world template (Meta test)</option>
            </select>
            <button type="submit" data-testid="wa-test-send-btn" className="flex items-center gap-1.5 bg-[#C5A059] text-[#1C1C1C] font-bold text-sm px-5 py-2.5 rounded-full hover:bg-[#d4b06c]">
              <Send className="w-3.5 h-3.5" /> Test Send
            </button>
          </form>
        )}

        <div className="mt-5 pt-5 border-t border-[#E5E7E5]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#5C6A63]">Sent Message Log</h3>
            <button onClick={loadWa} data-testid="wa-log-refresh-btn" className="flex items-center gap-1 text-xs font-semibold text-[#0A3F2F] hover:underline">
              <RefreshCcw className="w-3 h-3" /> Refresh
            </button>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="wa-log-list">
            {waLog.length === 0 && <p className="text-xs text-[#5C6A63]" data-testid="wa-log-empty">No messages sent via the API yet.</p>}
            {waLog.map((l) => (
              <div key={l.id} className="flex items-start justify-between gap-3 text-xs border-b border-[#E5E7E5] pb-2 last:border-0" data-testid={`wa-log-row-${l.id}`}>
                <div className="min-w-0">
                  <p className="font-semibold text-[#1C1C1C]">{l.kind} → {l.to}</p>
                  <p className="text-[#5C6A63] truncate">{l.error || l.preview}</p>
                </div>
                <div className="text-right shrink-0">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${WA_STATUS_STYLES[l.status] || WA_STATUS_STYLES.skipped}`}>{l.status}</span>
                  <p className="text-[10px] text-[#5C6A63] mt-1">{(l.created_at || "").slice(0, 16).replace("T", " ")}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
